// React
import { useEffect } from "react"

// Components
import { FooterMenu, Loading } from "components"
import { FormSection, Journal } from "./components"

// Form
import { FormProvider, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Third-Party Libraries
import toast from "react-hot-toast"
import { useNavigate, useParams } from "react-router-dom"

// Utils
import { useApi } from "utils"
import { useDetail, useInvoiceDetail } from "../ReturnRecord/utils"
import { type FormType, pageConfig, validationSchema, defaultValues } from "./utils"

export default function RefundReturnAdjustment(): JSX.Element {
  // Hooks
  const api = useApi()
  const invoice = useInvoiceDetail()
  const navigate = useNavigate()
  const { id } = useParams()
  const { data, isLoading } = useDetail(id)

  // Form
  const methods = useForm<FormType>({
    defaultValues: defaultValues,
    resolver: yupResolver(validationSchema)
  })
  const onSubmit = (value: FormType) => {
    
    const hasInvalidQty = value.refund_return_detail?.some((item) =>
      item.qty_return_item === undefined ||
      item.qty_return_item === null ||
      item.qty_return_item === 0
    )
  
    if (hasInvalidQty) {
      toast.error("Quantity refund cannot be empty or 0")
      return
    }
    if (!value.refund_return_detail || value.refund_return_detail.length === 0) {
      toast.error("No items will be refunded")
      return
    }

    return new Promise<void>((resolve) => {
      toast.promise(
        api.post(
          "/received/addrefundreturn",
          {
            ...value,
            return_id: id,
            type_refund: pageConfig.type_refund,
            customer_id: data?.datareturn.customer_id,
            location_id: data?.datareturn.location_id,
            address: data?.datareturn.address
          }
        ),
        {
          loading: "Loading...",
          success: (res) => res.data.message,
          error: (err) => err.response.data.detail?.message ?? err.response.data.message
        }
      ).then(() => {
        navigate(`/sales/transaction/return-record/${id}`)
      }).catch(() => {
      }).finally(() => {
        resolve()
      })
    })
  }

  useEffect(() => {
    if (data?.datareturn) {
      invoice.fetchData(data.datareturn.invoice_id)
    }

    // eslint-disable-next-line
  }, [data?.datareturn])

  return (
    <div className="container my-5 flex flex-col gap-3">
      {isLoading || !data ? (
        <Loading
          errorText="No data available"
          loading={isLoading}
        />
      ) : (
        <FormProvider {...methods}>
          <FormSection
            return={{
              address: data.datareturn.address,
              customer_id: data.datareturn.customer_id,
              customer_name: data.datareturn.customer_name,
              detailreturn: data.detailreturn,
              invoice_id: data.datareturn.invoice_id,
              location_id: data.datareturn.location_id,
              location_name: data.datareturn.location_name,
              return_status: data.datareturn.return_status,
              transaction_date: data.datareturn.transaction_date,
              trx_code: data.datareturn.trx_code
            }}
            invoice={{
              sales_order_id: invoice.data?.sales_order_id,
              trx_code: invoice.data?.trx_code,
              total_payment: invoice.data?.total_payment
            }}
            return_id={Number(id)}
          />

          <FooterMenu
            hasCancelButton
            save={{
              actiontype: "save",
              loading: methods.formState.isSubmitting ? "true" : undefined,
              onClick: methods.handleSubmit(onSubmit)
            }}
            customElement={{
              journal:<Journal/>
            }}
          />
        </FormProvider>
      )}
    </div>
  )
}